import axios from 'axios';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { CheckboxSc } from 'components/final-form/CheckBox';
import UpsertEmailCampaign from 'components/guarantors/emailcampaign/UpsertEmailCampaign';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import React, { useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Message, Modal, Table } from 'semantic-ui-react';
import {
  deleteCampaign,
  getActiveEmailCampaigns,
  getCampaign,
  getEmailCampaigns
} from 'services/emailCampaignServices';
import styled from 'styled-components';
import { EmailCampaignDto, UpsertEmailCampaignDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

const TableInfoContainer = styled.div`
  flex-grow: 1;
  cursor: context-menu;
  overflow: auto;
  margin-top: 15px;
  border: 1px solid rgba(34, 36, 38, .15);

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }
`;

interface Props extends MessageSourceProps {
  onSave: () => void,
  onCancel: () => void,
  emailCampaigns: EmailCampaignDto[]
}

const EmailCampaignsView = (props: Props) => {
  const {emailCampaigns, onCancel, getMessage} = props;

  const [foundCampaigns, setFoundCampaigns] =
      useState(emailCampaigns);

  const [includingInactive, setIncludingInactive] =
      useState(false);

  const [addEditModalOpen, setAddEditModalOpen] = useState(false);

  const [campaignId, setCampaignId] =
      useState(undefined as number | undefined);

  const [campaign, setCampaign] =
      useState(undefined as UpsertEmailCampaignDto | undefined);

  const [campaignsLoaded, setCampaignsLoaded] =
      useState(true);

  const [errorMessage, setErrorMessage] =
      useState(undefined as string | undefined);

  const [successfullySavedCampaign, setSuccessfullySavedCampaign] =
      useState(undefined as string | undefined);

  const [successfullyDeletedCampaign, setSuccessfullyDeletedCampaign] =
      useState(undefined as string | undefined);

  const cancelTokenSource = axios.CancelToken.source();

  const openAddEditModal = (id: number | undefined) => {

    if (id) {
      setCampaignId(id);

      getCampaign(id, cancelTokenSource)
          .then(response => {
            showModal(id, response);
          })
          .catch((e) => handleError(e.response.data))
    }
  }

  const showModal = (id?: number, campaign?: UpsertEmailCampaignDto) => {

    setCampaign(campaign);
    setAddEditModalOpen(true);
  }

  const closeAddEditModal = () => {

    setCampaign(undefined);
    setCampaignId(undefined);
    setAddEditModalOpen(false);
  }

  const saveCampaign = async (successfullySaved: boolean) => {

    if (successfullySaved) {
      setSuccessfullySavedCampaign(getMessage("emailCampaigns.successfullySavedCampaign.message"));

      const emailCampaigns = await getActiveEmailCampaigns(cancelTokenSource);
      setFoundCampaigns(emailCampaigns);
    }

    closeAddEditModal();
  }

  const deleteEmailCampaign = (id: number) => {

    setCampaignsLoaded(false);

    deleteCampaign(id, cancelTokenSource)
        .then(response => {
          const filteredCampaigns = foundCampaigns.filter(
              (campaign: EmailCampaignDto) => campaign.id != response.id);
          setFoundCampaigns(filteredCampaigns);
        })
        .catch((e) => handleError(e.response.data))
        .finally(() => {
          setCampaignsLoaded(true)
          setSuccessfullyDeletedCampaign(
              getMessage("emailCampaigns.successfullyDeletedCampaign.message"));
        });
  }


  const handleCheckBoxChange = async () => {

    setCampaignsLoaded(false);

    const newValue = !includingInactive;
    setIncludingInactive(newValue);

    getEmailCampaigns(newValue, cancelTokenSource)
        .then(response => {
          setFoundCampaigns(response)
        })
        .catch((e) => handleError(e.response.data))
        .finally(() => setCampaignsLoaded(true))
  }

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.unknownEmailCampaign
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const renderAddEditModal = (): React.ReactNode => {

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => closeAddEditModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("emailCampaign.header")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertEmailCampaign
                onSave={saveCampaign}
                onCancel={() => closeAddEditModal()}
                campaign={campaign}
                campaignId={campaignId}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

  const rowActionsCellRenderer = (campaign: EmailCampaignDto) => {
    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => openAddEditModal(campaign.id)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("emailCampaigns.confirmDelete")}
              onConfirm={() => deleteEmailCampaign(campaign.id)}
          />
        </React.Fragment>
    );
  }

  return (
      <>
        <FormModalContentContainer>
          {errorMessage &&
              <Grid.Row>
                <Grid.Column width={16}>
                  <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                    {errorMessage}
                  </StyledErrorMessage>
                </Grid.Column>
              </Grid.Row>
          }
          {successfullyDeletedCampaign &&
              <Message success onDismiss={() => setSuccessfullyDeletedCampaign(undefined)}>
                {successfullyDeletedCampaign}
              </Message>
          }
          {successfullySavedCampaign &&
              <Message success onDismiss={() => setSuccessfullySavedCampaign(undefined)}>
                {successfullySavedCampaign}
              </Message>
          }
          <PxGrid>
            <Grid.Row>
              <Grid.Column width={14}>
                <CheckboxSc
                    checked={includingInactive}
                    label={getMessage("includingInactive")}
                    toggle={true}
                    onChange={() => handleCheckBoxChange()}
                />
              </Grid.Column>
              <Grid.Column width={2}>
                <Button
                    type="button"
                    primary
                    compact
                    onClick={() => showModal()}
                >
                  {getMessage("button.add")}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </PxGrid>

          <TableInfoContainer>
            <Table compact="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{"#"}</Table.HeaderCell>
                  <Table.HeaderCell>{getMessage("name")}</Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("emailCampaigns.tableHeader.createdDate")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("emailCampaigns.tableHeader.createdByUser")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("emailCampaigns.tableHeader.rowActions")}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              {!campaignsLoaded
                  ? <LoaderComponent message={getMessage("loading")}/>
                  : <Table.Body>
                    {
                      foundCampaigns.map((campaign, index) => {
                        return (<Table.Row key={campaign.id}>
                          <Table.Cell>{index + 1}</Table.Cell>
                          <Table.Cell>{campaign.name}</Table.Cell>
                          <Table.Cell>{campaign.creationDate}</Table.Cell>
                          <Table.Cell>{campaign.createdByUserName}</Table.Cell>
                          <Table.Cell>{rowActionsCellRenderer(campaign)}</Table.Cell>
                        </Table.Row>)
                      })
                    }
                  </Table.Body>
              }
            </Table>
          </TableInfoContainer>
          <FormButtonsRow>
            <Button compact
                    type="button"
                    onClick={onCancel}
            >
              {getMessage("button.close")}
            </Button>
          </FormButtonsRow>
        </FormModalContentContainer>
        {addEditModalOpen && renderAddEditModal()}
      </>
  );
}

export default withMessages(EmailCampaignsView);
